import * as Sentry from "@sentry/browser";

const REDACTED = "[Filtered]";
const SECRET_KEY_RE = /cookie|authorization|jwt|token|refresh|password|passwd|secret|telegram|chat_id|bot|email|phone|comment|client/i;
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const TELEGRAM_TOKEN_RE = /\b\d{6,}:[A-Za-z0-9_-]{20,}\b/g;
const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const UA_PHONE_RE = /(?:\+?38)?0\d{9}\b/g;

let initialized = false;

function buildVersion() {
  return import.meta.env.VITE_APP_VERSION
    || import.meta.env.VITE_RELEASE
    || globalThis.__TORQO_BUILD_VERSION__
    || "dev";
}

function scrubString(value) {
  return String(value)
    .replace(JWT_RE, REDACTED)
    .replace(BEARER_RE, `Bearer ${REDACTED}`)
    .replace(TELEGRAM_TOKEN_RE, REDACTED)
    .replace(EMAIL_RE, REDACTED)
    .replace(UA_PHONE_RE, REDACTED);
}

function scrub(value, depth = 0) {
  if (depth > 8) return REDACTED;
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return scrubString(value);
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((item) => scrub(item, depth + 1));

  const result = {};
  for (const [key, item] of Object.entries(value)) {
    if (SECRET_KEY_RE.test(key)) {
      result[key] = REDACTED;
      continue;
    }
    result[key] = scrub(item, depth + 1);
  }
  return result;
}

function sanitizeEvent(event) {
  const clean = scrub(event);
  if (clean.request) {
    const route = routeFromUrl(clean.request.url || "");
    clean.request.cookies = undefined;
    clean.request.headers = scrub(clean.request.headers || {});
    clean.request.data = /\/auth\/(login|signup)/i.test(route) ? REDACTED : scrub(clean.request.data || {});
  }
  if (clean.user) clean.user = undefined;
  return clean;
}

function routeFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.replace(/\/\d+(?=\/|$)/g, "/:id");
  } catch {
    return String(url || "").split("?")[0].replace(/\/\d+(?=\/|$)/g, "/:id");
  }
}

function featureFromRoute(route) {
  if (route.includes("/auth/")) return "auth";
  if (route.includes("/bookings") && route.includes("/review")) return "review";
  if (route.includes("/bookings")) return "booking";
  if (route.includes("/businesses") && route.includes("/slots")) return "map";
  if (route.includes("/businesses")) return "businesses";
  if (route.includes("/owner/")) return "business_dashboard";
  if (route.includes("/admin")) return "admin";
  if (route.includes("/support")) return "support";
  return "api";
}

export function initErrorMonitoring() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || initialized) return false;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || "development",
    release: buildVersion(),
    sendDefaultPii: false,
    tracesSampleRate: 0.1,
    beforeSend(event) {
      return sanitizeEvent(event);
    },
  });

  Sentry.setTag("build_version", buildVersion());
  Sentry.setTag("page", location.pathname || "/");

  window.addEventListener("unhandledrejection", (event) => {
    captureError(event.reason || new Error("Unhandled promise rejection"), {
      feature: "runtime",
      page: location.pathname,
    });
  });

  initialized = true;
  return true;
}

export function captureError(error, context = {}) {
  if (!initialized) return;
  Sentry.withScope((scope) => {
    const tags = {
      page: context.page || location.pathname || "/",
      feature: context.feature,
      business_id: context.business_id,
      api_route: context.api_route,
      http_status: context.http_status,
      build_version: buildVersion(),
    };
    Object.entries(tags).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") scope.setTag(key, String(value));
    });
    if (context.extra) scope.setContext("torqo", scrub(context.extra));
    Sentry.captureException(error instanceof Error ? error : new Error(String(error || "Unknown error")));
  });
}

export function captureApiError(error, route, status, extra = {}) {
  if (Number(status) < 500) return;
  captureError(error, {
    feature: featureFromRoute(route),
    api_route: routeFromUrl(route),
    http_status: status,
    business_id: extra.business_id || extra.businessId,
    extra,
  });
}

export function captureFeatureError(feature, error, extra = {}) {
  captureError(error, {
    feature,
    page: location.pathname,
    business_id: extra.business_id || extra.businessId,
    extra,
  });
}

export function captureMapError(error, extra = {}) {
  captureFeatureError("map", error, extra);
}

export function isSentryEnabled() {
  return initialized;
}
